<script lang="ts">
    import type {DonutChartDataPoint} from 'shared/components/kp-charts/types';
    import type {CssSize} from 'shared/ui-widgets/types';
    import {tweened} from 'svelte/motion';
    import {cubicOut} from 'svelte/easing';
    import {onMount} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';

    export let data: DonutChartDataPoint[] = [];
    export let size: CssSize;
    export let strokeWidth = 2.5;
    export let withoutSideLegend = false;

    let hoveringOver: string | null = null;
    const hoveringStrokeWidth = strokeWidth * 1.5;

    const progressTween = tweened(0, {
        duration: 1000,
        easing: cubicOut
    });

    const valueTween = tweened(0, {
        duration: 1000,
        easing: cubicOut
    });

    onMount(() => {
        progressTween.set(1);
        valueTween.set(getSummedUpValues());
    });

    function calculateDashOffset(index: number, segmentProgress: number): number {
        let offset = 0;

        for (let i = 0; i < index; i++) {
            offset += (normalizeValue(data[i].value) * segmentProgress);
        }

        return 100 - offset;
    }

    function sortDataByValue(): DonutChartDataPoint[] {
        return data.toSorted((a, b) => b.value - a.value);
    }

    function getSummedUpValues(): number {
        return data.reduce((acc, value) => acc + value.value, 0);
    }

    function normalizeValue(value: number): number {
        return value / getSummedUpValues() * 100;
    }

    function getHoveringValue(label: string | null): number | null {
        return label ? data.find((dataPoint) => dataPoint.label === label)?.value : null;
    }

    const handleHover = (label: string | null) => {
        hoveringOver = label;
        valueTween.set(getHoveringValue(label) ?? getSummedUpValues());
    };
</script>

<div class="kp-donut-chart-container">
    <div class="donut-chart" style="width: {size}; height: {size};">

        <svg width="100%" height="100%" viewBox="0 0 42 42" class="donut">
            <circle class="donut-hole"
                    cx="21"
                    cy="21"
                    r="15.91549430918954">
            </circle>

            <circle class="donut-ring"
                    cx="21"
                    cy="21"
                    r="15.91549430918954"
                    fill="transparent"
                    stroke="var(--outline-color)"
                    stroke-width="{strokeWidth}">
            </circle>

            {#each data as {color, value, label}, i (label)}
                <circle style="--hoverStrokeWidth: {hoveringStrokeWidth}"
                        class="donut-segment {hoveringOver ? (hoveringOver === label ? 'hovering' : 'not-hovering') : ''}"
                        cx="21"
                        cy="21"
                        r="15.91549430918954"
                        fill="transparent"
                        stroke={color}
                        stroke-width="{strokeWidth}"
                        stroke-dasharray="{normalizeValue(value) * $progressTween} {100 - normalizeValue(value) * $progressTween}"
                        stroke-dashoffset="{calculateDashOffset(i, $progressTween)}">
                </circle>
            {/each}
        </svg>

        <div class="legend-container">
            <slot data="{sortDataByValue()}" showedValue="{Math.round($valueTween)}"/>
        </div>
    </div>

    {#if !withoutSideLegend}
        <ul class="side-legend-container">
            {#each data as {color, value, label}}
                <li class="side-legend-item"
                    class:not-hovering={exists(hoveringOver) && hoveringOver !== label}
                    on:mouseenter={() => handleHover(label)}
                    on:mouseleave={() => handleHover(null)}>

                    <div class="item-color-box" style:background-color="{color}"></div>
                    <span class="item-label">{label}</span>
                    <span class="item-value">{value}</span>
                </li>
            {/each}
        </ul>
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .kp-donut-chart-container {
        display: flex;
        gap: @spacing-xxl;
        align-items: center;

        .donut-chart {
            flex-shrink: 1;
            position: relative;
            display: inline-block;

            .donut-hole {
                fill: @themed-body-bg;
            }

            .donut-ring {
                stroke: @themed-border-muted;
            }

            .donut-segment {
                transition: stroke-width 0.3s ease-in-out, opacity 0.3s ease-in-out;

                &.hovering {
                    stroke-width: var(--hoverStrokeWidth);
                }

                &.not-hovering {
                    opacity: 0.25;
                }
            }

            .legend-container {
                display: flex;
                flex-direction: column;
                position: absolute;
                align-items: center;
                justify-content: center;
                text-align: center;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }
        }

        .side-legend-container {
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            .side-legend-item {
                display: flex;
                gap: @spacing-sm;
                align-items: center;
                position: relative;
                cursor: pointer;
                transition: opacity 0.3s ease-in-out;
                isolation: isolate;

                &:before {
                    content: '';
                    display: block;
                    right: calc(-1 * @spacing-s);
                    left: calc(-1 * @spacing-s);
                    top: calc(-1 * @spacing-s);
                    background-color: @themed-panel-bg;
                    position: absolute;
                    opacity: 0;
                    z-index: -1;
                    visibility: hidden;
                    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
                }

                &:not(:last-child) {
                    padding-bottom: @spacing-ml;

                    &:before {
                        bottom: calc(-1 * @spacing-s + @spacing-ml);
                    }
                }

                &:last-child:before {
                    bottom: calc(-1 * @spacing-s);
                }

                &.not-hovering {
                    opacity: 0.25;
                }

                &:hover {
                    &:before {
                        opacity: 1;
                        visibility: visible;
                    }
                }

                .item-color-box {
                    width: 16px;
                    height: 16px;
                    border: 1px solid rgba(0, 0, 0, 0.2);
                    border-radius: @border-radius-sm;
                }

                .item-value {
                    font-size: @font-size-small;
                    color: @themed-text-muted;
                }
            }
        }
    }
</style>